import { UAParser } from 'ua-parser-js';
import { Cookies } from '@fff-web/fff-utilities';
import type { GetGenericTrackingDataProps } from '../../interfaces';
import { getEnv } from './environment';
import SESSION_INFO_KEY from './sessionInfoKey';

/**
 * Represents the data structure as it is stored in the JSON cookie.
 * Dates are stored as ISO strings for serialization.
 */
interface SessionCookieData {
  sessionId: string;
  sessionCreationDate: string;
  lastActivity: string;
}

/**
 * Pure function: Validates the structure of session data parsed from a cookie.
 */
const isValidSessionData = (data: unknown): data is SessionCookieData => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  return (
    'sessionId' in data &&
    typeof data.sessionId === 'string' &&
    data.sessionId.length > 0 &&
    'sessionCreationDate' in data &&
    typeof data.sessionCreationDate === 'string' &&
    data.sessionCreationDate.length > 0 &&
    'lastActivity' in data &&
    typeof data.lastActivity === 'string' &&
    data.lastActivity.length > 0
  );
};

/**
 * Reads and validates the session from the `sm_session_info` cookie.
 * Returns the session data if it's valid, otherwise returns null.
 * This function handles the conversion from string-based cookie data to Date objects.
 */
export const getCurrentSession = (): GetGenericTrackingDataProps | null => {
  const sessionCookieRaw = Cookies.getCookie(SESSION_INFO_KEY);
  if (!sessionCookieRaw) {
    return null;
  }

  try {
    const sessionCookieParsed: unknown = JSON.parse(sessionCookieRaw);

    if (!isValidSessionData(sessionCookieParsed)) {
      return null;
    }

    return {
      device: getDevice(),
      sessionId: sessionCookieParsed.sessionId,
      sessionCreationDate: new Date(sessionCookieParsed.sessionCreationDate),
      lastActivity: new Date(sessionCookieParsed.lastActivity),
    };
  } catch (_) {
    return null;
  }
};

/**
 * Factory function to create a new session object.
 * It immediately writes the new session to a cookie.
 */
export const createNewSession = (): GetGenericTrackingDataProps => {
  const now = new Date();
  const sessionData: GetGenericTrackingDataProps = {
    device: getDevice(),
    sessionId: getUuid(),
    sessionCreationDate: now,
    lastActivity: now,
  };

  writeSessionCookie(sessionData);

  return sessionData;
};

/**
 * Helper function to write session data to the `sm_session_info` cookie.
 * This function handles the conversion from Date objects to ISO strings for serialization.
 */
const writeSessionCookie = (sessionData: GetGenericTrackingDataProps): void => {
  const cookieData: SessionCookieData = {
    sessionId: sessionData.sessionId,
    sessionCreationDate: sessionData.sessionCreationDate.toISOString(),
    lastActivity: sessionData.lastActivity.toISOString(),
  };

  Cookies.setCookie({
    key: SESSION_INFO_KEY,
    value: JSON.stringify(cookieData),
    path: '/',
    maxAge: 30 * 60,
    domain: getEnv().originUrl,
  });
};

/**
 * Checks if a session has expired based on the last activity timestamp.
 * A session is considered expired if there has been no activity for 30 minutes.
 */
const isSessionExpired = (lastActivity: Date): boolean => {
  const now = new Date();
  const thirtyMinutesInMs = 30 * 60 * 1000;
  return now.getTime() - lastActivity.getTime() > thirtyMinutesInMs;
};

/**
 * Debounce mechanism to limit cookie writes.
 * Only writes to cookie once every 5 seconds
 */
const shouldWriteCookie = (() => {
  let lastCookieWrite = 0;
  const COOKIE_WRITE_INTERVAL = 5000;

  return () => {
    const now = Date.now();
    if (now - lastCookieWrite >= COOKIE_WRITE_INTERVAL) {
      lastCookieWrite = now;
      return true;
    }
    return false;
  };
})();

/**
 * Updates the last activity timestamp of the current session.
 * If no session exists, creates a new one.
 * If the session is expired, creates a new one.
 * This should be called on any user interaction that should extend the session.
 * Uses debouncing to limit cookie writes to once every 5 seconds.
 * @returns The current session data
 */
export const updateSessionActivity = (): GetGenericTrackingDataProps => {
  const existingSession = getCurrentSession();

  if (!existingSession) {
    return createNewSession();
  }

  if (isSessionExpired(existingSession.lastActivity)) {
    return createNewSession();
  }

  if (!shouldWriteCookie()) {
    return existingSession;
  }

  // Session exists, is not expired, and enough time has passed since last write (5 seconds)
  // update the lastActivity timestamp
  const updatedSession = {
    ...existingSession,
    lastActivity: new Date(),
  };

  writeSessionCookie(updatedSession);
  return updatedSession;
};

/**
 * Main public API for getting an active session.
 */
export const getActiveSession = (): GetGenericTrackingDataProps => {
  const existingSession = getCurrentSession();

  if (existingSession && !isSessionExpired(existingSession.lastActivity)) {
    return existingSession;
  }

  return createNewSession();
};

/**
 * Legacy compatibility function that maintains the existing API.
 * This function simply wraps the new `getActiveSession` function.
 * @deprecated Use `getActiveSession()` instead. This function will be removed in a future version.
 */
export const getGenericTrackingData = (): GetGenericTrackingDataProps => {
  return getActiveSession();
};

export const getDevice = (): string | undefined => {
  const deviceRegex = [
    [/(Intel Mac OS|PPC Mac OS)/i],
    [
      [UAParser.DEVICE.VENDOR, 'Apple'],
      [UAParser.DEVICE.TYPE, 'Desktop'],
    ],
    [/(Windows NT|Linux i686)/i],
    [[UAParser.DEVICE.TYPE, 'Desktop']],
    [/(Linux armv7l|Mobile)/i],
    [[UAParser.DEVICE.TYPE, 'Mobile Web']],
    [/(Tablet|iPad)/i],
    [[UAParser.DEVICE.TYPE, 'Tablet Web']],
  ];
  const parser = new UAParser(undefined, { device: deviceRegex });
  const { type } = parser.getResult().device;
  return type;
};

// Matches legacy session IDs
export const getUuid = (): string => {
  let d = new Date().getTime();
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    d += performance.now(); // use high-precision timer if available
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};
